import { useEffect, useRef } from 'react';
import { ConversationItem } from './ConversationItem';
import { SearchInput, SkeletonText, SkeletonBox } from '@/components';

export function ChatSidebar({
  conversations,
  isLoading,
  activeConversationId,
  onSelectConversation,
  windowFilter,
  setWindowFilter,
  unreadOnly,
  setUnreadOnly,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  searchQuery,
  onSearchChange,
  platform = 'whatsapp',
}) {
  const sentinelRef = useRef(null);

  // IntersectionObserver: fetch next page when sentinel enters view
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage?.();
        }
      },
      { threshold: 0.5 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, isLoading]);

  // Conversations are already filtered by the server when using dynamic search
  const filteredConversations = conversations;

  return (
    <div
      className={`flex flex-col h-full min-h-0 w-full min-w-0 bg-layer-2-sidebar border-r border-border-default ${
        platform === 'whatsapp' ? 'wa-inbox-sidebar' : ''
      }`}
    >
      {/* Sidebar Header */}
      <div className="wa-inbox-toolbar p-4 pb-3 border-b border-border-default">
        {/* Search */}
        <div>
          <SearchInput
            type="text"
            placeholder="Search conversations by name or phone"
            value={searchQuery}
            onChange={(e) => onSearchChange?.(e.target.value)}
            inputClassName="h-10 rounded-lg bg-[#F5F5F5] border-transparent placeholder:text-text-muted"
          />
        </div>

        {/* Window filter (exclusive) + Unread (stacks with window filter) */}
        <div className="mt-3 flex items-center gap-1.5 w-full">
          {(platform === 'email' ? [''] : ['', 'active', 'inactive']).map(
            (filterType) => {
              const isSelected = windowFilter === filterType;
              const label =
                filterType === ''
                  ? 'All'
                  : filterType === 'active'
                    ? 'Active'
                    : 'Expired';

              return (
                <button
                  key={filterType}
                  type="button"
                  aria-pressed={isSelected}
                  onClick={() => setWindowFilter?.(filterType)}
                  className={`flex-1 text-center justify-center py-1.5 px-1 sm:px-2 rounded-[8px] text-xs font-medium whitespace-nowrap transition-colors outline-none focus-visible:ring-2 focus-visible:ring-primary/40 ${
                    isSelected
                      ? 'bg-primary text-white'
                      : 'bg-[#F5F5F5] text-text-muted hover:text-text-primary border border-transparent'
                  }`}
                >
                  {label}
                </button>
              );
            },
          )}
          <button
            type="button"
            aria-pressed={!!unreadOnly}
            onClick={() => setUnreadOnly?.(!unreadOnly)}
            className={`flex-1 text-center justify-center py-1.5 px-1 sm:px-2 rounded-[8px] text-xs font-medium whitespace-nowrap transition-colors outline-none focus-visible:ring-2 focus-visible:ring-primary/40 ${
              unreadOnly
                ? 'bg-primary text-white'
                : 'bg-[#F5F5F5] text-text-muted hover:text-text-primary border border-transparent'
            }`}
          >
            Unread
          </button>
        </div>
      </div>

      {/* Conversation List */}
      <div className="wa-inbox-list flex-1 min-h-0 overflow-y-auto overflow-x-hidden custom-scrollbar py-4">
        {isLoading || !conversations ? (
          // Skeleton Loading — matches new card design
          [...Array(10)].map((_, i) => (
            <div
              key={`skel-${i}`}
              className="flex items-center gap-3 px-3 py-3 mx-2 rounded-2xl mb-1 border border-transparent"
            >
              <SkeletonBox
                className="w-11 h-11 shrink-0"
                rounded="rounded-full"
              />
              <div className="flex-1">
                <div className="flex justify-between mb-2">
                  <SkeletonText width="w-28" height="h-3.5" />
                  <SkeletonText width="w-10" height="h-3" />
                </div>
                <SkeletonText width="w-40" height="h-3" />
              </div>
            </div>
          ))
        ) : filteredConversations?.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full p-6 text-center text-text-muted">
            <div className="w-16 h-16 mb-4 bg-input-background rounded-full flex items-center justify-center">
              <svg
                className="w-8 h-8 opacity-50"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                />
              </svg>
            </div>
            <p className="text-sm">No conversations found</p>
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            {filteredConversations?.map((conv) => (
              <ConversationItem
                key={conv.id}
                {...conv}
                platform={platform}
                isActive={activeConversationId === conv.id}
                onClick={() => onSelectConversation(conv)}
              />
            ))}
            {/* Sentinel element for intersection observer */}
            <div ref={sentinelRef} className="w-full shrink-0 min-h-[20px]">
              {isFetchingNextPage && (
                <div className="flex flex-col gap-2 w-full mt-1">
                  {[...Array(2)].map((_, i) => (
                    <div
                      key={`skel-next-${i}`}
                      className="flex items-center gap-3 px-3 py-3 mx-2 rounded-2xl mb-1 border border-transparent"
                    >
                      <SkeletonBox
                        className="w-11 h-11 shrink-0"
                        rounded="rounded-full"
                      />
                      <div className="flex-1">
                        <div className="flex justify-between mb-2">
                          <SkeletonText width="w-28" height="h-3.5" />
                          <SkeletonText width="w-10" height="h-3" />
                        </div>
                        <SkeletonText width="w-40" height="h-3" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
